/**
 * Tracked share links for the share sheet.
 *
 * Every shared URL should be a short link (`/api/go/<code>`) so the
 * click can be counted and a later signup attributed. The link is
 * minted on the click itself — the public endpoint is rate-limited, so
 * minting one per platform on every sheet open would burn the budget —
 * and when the mint fails or is slow, the canonical URL with a `?src=`
 * tag goes out instead. A share is never blocked on analytics.
 */

import { socialPublicApi, shareTargetBody } from '@/features/social/api/socialPublicApi';
import { shareTargetUrl, type SharePlatform, type ShareTarget } from '@/utils/share';

/** How long a mint may take before the fallback URL is shared instead; browsers only honour the click for a few seconds. */
export const MINT_TIMEOUT_MS = 2500;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('share link timed out')), ms);
        promise.then(
            (value) => { clearTimeout(timer); resolve(value); },
            (error) => { clearTimeout(timer); reject(error); },
        );
    });
}

/** The URL to share: the tracked short link, or the tagged canonical URL when minting fails. */
export async function resolveShareUrl(target: ShareTarget, platform: SharePlatform): Promise<string> {
    try {
        const link = await withTimeout(
            socialPublicApi.mintShareLink({ ...shareTargetBody(target), platform }),
            MINT_TIMEOUT_MS,
        );
        return link.url || shareTargetUrl(target, platform);
    } catch {
        return shareTargetUrl(target, platform);
    }
}

/** Count the click, fire-and-forget. */
export function reportShare(target: ShareTarget, platform: SharePlatform, src?: string): void {
    void socialPublicApi
        .recordShareEvent({ ...shareTargetBody(target), platform, src })
        .catch(() => undefined);
}
